import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
    Box,
    Divider,
    Drawer,
    useMediaQuery,
    List,
    Collapse,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItem,
    Button,
    IconButton,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Person2Icon from "@mui/icons-material/Person2";
import { ChartBar as ChartBarIcon } from "../icons/chart-bar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import CasinoIcon from "@mui/icons-material/Casino";
import TuneIcon from "@mui/icons-material/Tune";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PaidIcon from "@mui/icons-material/Paid";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsIcon from "@mui/icons-material/Settings";
import ApiIcon from "@mui/icons-material/Api";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CachedIcon from "@mui/icons-material/Cached";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ChatIcon from "@mui/icons-material/Chat";
import { useEffect, useState } from "react";
import { NavItem } from "./nav-item";
import { Logo } from "./logo";
import { useSystemSetting } from "../context/SystemSettingContext";
import { Users } from "../icons/users";
import { t } from "i18next";
import { isFeatureEnabled } from "../utils/serverFeatures";
import {
    HappyAgencyIcon, HappyApiIcon, HappyAuditIcon, HappyBellIcon, HappyCrownIcon,
    HappyDashboardIcon, HappyDepositIcon, HappyGameIcon, HappyGiftIcon, HappyHistoryIcon,
    HappyPromoIcon, HappyRefundIcon, HappySiteIcon, HappyStaffIcon, HappyUsersIcon
} from "./happy-sidebar-icons";

// Render menu đa tầng (Minigame -> Xổ số / Tài xỉu / Baccarat / Xóc đĩa / Bầu cua / Rồng hổ)
const SidebarMenuItem = ({ item, level = 0, onItemClick }) => {
    const location = useLocation();
    const normalizePath = (path) => String(path || "").replace(/\/$/, "") || "/";
    const isGroupMatch = (itemHref, currentPath) => {
        if (!itemHref || !currentPath) return false;
        const [targetPath] = itemHref.split("?");
        const normTarget = normalizePath(targetPath).toLowerCase();
        const normCurrent = normalizePath(currentPath).toLowerCase();

        if (normTarget.includes("setxd") || normTarget.includes("set-xd")) {
            return normCurrent.includes("setxd") || normCurrent.includes("set-xd");
        }
        if (normTarget.includes("settx") || normTarget.includes("set-tx")) {
            return normCurrent.includes("settx") || normCurrent.includes("set-tx");
        }
        if (normTarget.includes("setbaccarat")) {
            return normCurrent.includes("setbaccarat");
        }
        if (normTarget.includes("setbaucua")) {
            return normCurrent.includes("setbaucua");
        }
        if (normTarget.includes("setrongho")) {
            return normCurrent.includes("setrongho");
        }
        if (normTarget.includes("setxs")) {
            return normCurrent.includes("setxs");
        }
        return false;
    };

    const matchesHref = (href) => {
        if (!href) return false;
        const [pathname, search = ""] = href.split("?");
        if (isGroupMatch(href, location.pathname)) return true;
        if (normalizePath(location.pathname) !== normalizePath(pathname)) return false;
        return search ? location.search === `?${search}` : !location.search;
    };
    const includesActivePath = (entry) => {
        if (matchesHref(entry.href)) return true;
        return Boolean(entry.children?.some(includesActivePath));
    };
    const [open, setOpen] = useState(() => includesActivePath(item));

    const hasChildren = item.children && item.children.length > 0;

    useEffect(() => {
        // Keep only the branch containing the current URL expanded. This
        // avoids stale submenu branches remaining visible after navigation.
        setOpen(includesActivePath(item));
    }, [location.pathname, location.search]);

    const handleToggle = (e) => {
        if (hasChildren) {
            e.preventDefault();
            e.stopPropagation();
            setOpen((prev) => !prev);
        }
    };

    if (hasChildren) {
        return (
            <Box sx={{ width: "100%" }}>
                <ListItem
                    disableGutters
                    className="happy-sidebar-parent"
                    sx={{
                        display: "flex",
                        width: level === 0 ? "calc(100% - 8px)" : "100%",
                        height: "45px",
                        mb: 0,
                        mx: level === 0 ? 0.5 : 0,
                        py: 0,
                        px: 0,
                        pl: level > 0 ? (level * 2) : 0
                    }}>
                    <Button
                        component="div"
                        startIcon={item.icon || <SportsEsportsIcon fontSize="small" />}
                        endIcon={open ? <ExpandLess sx={{ color: "#FFFFFF !important" }} /> : <ExpandMore sx={{ color: "#FFFFFF !important" }} />}
                        disableRipple
                        onClick={handleToggle}
                        sx={{
                            background: "transparent",
                            borderRadius: "8px",
                            color: "#FFFFFF !important",
                            fontWeight: 600,
                            letterSpacing: 0,
                            fontSize: "15px",
                            justifyContent: "flex-start",
                            px: 3,
                            py: 0,
                            height: "45px",
                            textAlign: "left",
                            textTransform: "none",
                            width: "100%",
                            cursor: "pointer",
                            "& .MuiButton-startIcon": {
                                color: "#FFFFFF !important",
                                width: "24px",
                                marginRight: "20px",
                                fontSize: "20px"
                            },
                            "&:hover": {
                                background: "rgba(255, 255, 255, 0.2)"
                            }
                        }}>
                        <Box sx={{ flexGrow: 1, color: "#FFFFFF !important" }}>{item.title}</Box>
                    </Button>
                </ListItem>

                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pt: 0.2, pb: 0.2 }}>
                        {item.children.map((child) => (
                            <SidebarMenuItem key={child.title} item={child} level={level + 1} onItemClick={onItemClick} />
                        ))}
                    </List>
                </Collapse>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", pl: level > 1 ? (level * 2) : 0 }}>
            <NavItem icon={item.icon} to={item.href} title={item.title} onClick={onItemClick} />
        </Box>
    );
};

export const DashboardSidebar = (props) => {
    const getSanhList = (basePath) => {
        const letters = ["A", "B", "C", "D", "E", "F"];
        return [1, 2, 3, 4, 5, 6].map((num) => ({
            href: `${basePath}${num}`,
            icon: <SportsEsportsIcon fontSize="small" />,
            title: `Sảnh ${letters[num - 1]}`
        }));
    };

    const getRoomList = (basePath, count = 6) => {
        const letters = ["A", "B", "C", "D", "E", "F"];
        const arr = [];
        for (let i = 1; i <= count; i++) {
            arr.push({
                href: `${basePath}${i}`,
                icon: <SportsEsportsIcon fontSize="small" />,
                title: `Sảnh ${letters[i - 1]}`
            });
        }
        return arr;
    };

    const items = [
        { href: "/admin", icon: <HappyDashboardIcon />, title: "Dashboard", perm: "dashboard" },
        {
            title: "Quản lý nạp rút", icon: <HappyDepositIcon />, perm: ["recharge", "withdraw", "money", "approval_history"],
            children: [
                { href: "/admin/add", icon: <PaidIcon fontSize="small" />, title: "Danh sách nạp điểm", perm: "recharge" },
                { href: "/admin/request", icon: <CurrencyExchangeIcon fontSize="small" />, title: "Danh sách rút điểm", perm: "withdraw" },
                { href: "/admin/approval-history", icon: <HappyHistoryIcon />, title: "Lịch sử duyệt", perm: "approval_history" }
            ]
        },
        { href: "/admin/users", icon: <HappyUsersIcon />, title: "Quản lý người chơi", perm: ["users", "edit"] },
        {
            title: "Minigame", icon: <HappyGameIcon />, perm: ["set", "set_xs", "set_tx", "set_baccarat", "set_xd", "set_baucua", "set_rongho"],
            children: [
                { href: "/admin/setxs75s", title: "Xổ số", perm: "set_xs" },
                { href: "/admin/settx60s", title: "Tài xỉu", perm: "set_tx" },
                { href: "/admin/setbaccarat60s?sanh=1", title: "Baccarat", perm: "set_baccarat" },
                { href: "/admin/setxdblockchain60s?sanh=1", title: "Xóc đĩa", perm: "set_xd" },
                { href: "/admin/setbaucua60s?sanh=1", title: "Bầu cua", perm: "set_baucua" },
                { href: "/admin/setrongho60s?sanh=1", title: "Rồng hổ", perm: "set_rongho" }
            ]
        },
        { href: "/admin/history", icon: <HappyHistoryIcon />, title: "Lịch sử đặt cược", perm: "history" },
        { href: "/admin/khuyenmai", icon: <HappyPromoIcon />, title: "Quản lý khuyến mãi", perm: "khuyenmai" },
        {
            title: "Giftcode", icon: <HappyGiftIcon />, perm: "giftcode",
            children: [
                { href: "/admin/promo", title: "Giftcode", perm: "giftcode" },
            ]
        },
        {
            title: "Quản lý hoàn trả", icon: <HappyRefundIcon />, perm: ["refund", "khuyenmai", "history"],
            children: [
                { href: "/admin/refund", title: "Lịch sử hoàn trả", perm: "refund" }
            ]
        },
        {
            title: "Giới thiệu người chơi", icon: <HappyCrownIcon />, perm: ["aff_config", "config"],
            children: [
                { href: "/admin/setting/money", title: "Cài đặt hoa hồng", perm: "aff_config" },
            ]
        },
        {
            title: "Quản lý VIP", icon: <HappyCrownIcon />, perm: ["vip_profit", "vip_history", "config", "users"],
            children: [
                { href: "/admin/setting/profit", title: "Cấp độ", perm: "vip_profit" },
                { href: "/admin/users?view=vip-history", title: "Lịch sử thăng cấp", perm: "vip_history" }
            ]
        },
        {
            title: "Thông báo", icon: <HappyBellIcon />, perm: ["notify_msg", "notify_setting", "notification"],
            children: [
                { href: "/admin/notification", title: "Tin nhắn", perm: "notify_msg" },
                { href: "/admin/settingnotify", title: "Thông báo", perm: "notify_setting" }
            ]
        },
        {
            title: "Đại lý", icon: <HappyAgencyIcon />, perm: ["agency_list", "employee"],
            children: [
                { href: "/admin/allNV", title: "Danh sách đại lý", perm: "agency_list" },
            ]
        },
        {
            title: "Nhân viên", icon: <HappyStaffIcon />, perm: ["staff_list", "staff_rbac", "employee"],
            children: [
                { href: "/admin/admin-list", title: "Danh sách nhân viên", perm: "staff_list" },
                { href: "/admin/permissions", title: "Phân quyền (RBAC)", perm: "staff_rbac" }
            ]
        },
        { href: "/admin/audit-logs", icon: <HappyAuditIcon />, title: "Lịch sử thao tác", perm: "logs" },
        {
            title: "Quản lý game API", icon: <HappyApiIcon />, perm: ["api_report", "api_transfer", "api_lobbies", "api_presets", "api_game"],
            children: [
                { href: "/admin/historyapi", title: "Báo cáo game API", perm: "api_report" },
                { href: "/admin/report-api/history-transfer", title: "Lịch sử chuyển quỹ", perm: "api_transfer" },
                { href: "/admin/lobbies", title: "Cấu hình sảnh game", perm: "api_lobbies" },
                { href: "/admin/game-presets", title: "Tỉ lệ sảnh (Kill API)", perm: "api_presets" }
            ]
        },
        {
            title: "Quản lý trang", icon: <HappySiteIcon />, perm: ["site_custom", "site_setting", "site_bank", "site_login_logs", "site_online_users", "config", "bank", "logs", "users"],
            children: [
                { href: "/admin/config?tab=general", title: "Tùy chỉnh hệ thống", perm: "site_custom" },
                { href: "/admin/setting", title: "Cài đặt chung", perm: "site_setting" },
                { href: "/admin/bank", title: "Quản lý ngân hàng", perm: "site_bank" },
                { href: "/admin/login-history", title: "Lịch sử đăng nhập", perm: "site_login_logs" },
                { href: "/admin/online-users", title: "User Online", perm: "site_online_users" }
            ]
        },
        { href: "/admin/chat", icon: <ChatIcon fontSize="small" />, title: "Hộp thư CSKH", perm: "cskh_chat" }
    ];

    const { setting } = useSystemSetting();

    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
        defaultMatches: true,
        noSsr: false
    });

    const [open, setOpen] = useState(props.open);

    useEffect(() => {
        setOpen(props.open);
    }, [props.open]);

    const sendData = () => {
        props.callback && props.callback(false);
        props.onClose && props.onClose();
    };

    const handleNavItemClick = () => {
        if (!lgUp) {
            sendData();
        }
    };

    const [profile, setProfile] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("profile"));
        } catch {
            return null;
        }
    });

    useEffect(() => {
        const timer = setInterval(() => {
            const storedProfile = JSON.parse(localStorage.getItem("profile"));
            if (storedProfile) {
                setProfile(storedProfile);
            }
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, []);

    // Bản đồ tương thích ngược cho các tài khoản cũ
    const LEGACY_MAP = {
        set_xs: ["set"],
        set_tx: ["set"],
        set_baccarat: ["set"],
        set_xd: ["set"],
        set_baucua: ["set"],
        set_rongho: ["set"],
        refund: ["khuyenmai", "history"],
        aff_config: ["config"],
        vip_profit: ["config"],
        vip_history: ["users"],
        notify_msg: ["notification"],
        notify_setting: ["notification"],
        cskh_chat: ["notification"],
        agency_list: ["employee"],
        staff_list: ["employee"],
        staff_rbac: ["employee"],
        api_report: ["api_game"],
        api_transfer: ["api_game"],
        api_lobbies: ["api_game"],
        api_presets: ["api_game"],
        site_custom: ["config"],
        site_setting: ["config"],
        site_bank: ["bank"],
        site_login_logs: ["logs"],
        site_online_users: ["users"],
        approval_history: ["recharge", "withdraw", "money"],
    };

    const isPermitted = (itemPerm) => {
        if (profile?.permission?.full == 1 || profile?.iduser === 1) return true;
        if (!profile?.permission) return false;
        if (!itemPerm) return true;

        const checkSingleKey = (k) => {
            // 1. Kiểm tra trực tiếp theo key chi tiết
            if (Number(profile.permission[k]) === 1 || profile.permission[k] === true) return true;
            if (Number(profile.permission[k]) === 0 || profile.permission[k] === false) {
                return false;
            }
            // 2. Tương thích ngược: nếu key mới chưa được lưu, kiểm tra key cha cũ
            if (LEGACY_MAP[k]) {
                return LEGACY_MAP[k].some((legKey) => Number(profile.permission[legKey]) === 1 || profile.permission[legKey] === true);
            }
            return false;
        };

        if (Array.isArray(itemPerm)) {
            return itemPerm.some(checkSingleKey);
        }
        return checkSingleKey(itemPerm);
    };

    const filterNavItems = (menuList) => {
        if (profile?.permission?.full == 1 || profile?.iduser === 1) return menuList;
        return menuList
            .map((item) => {
                if (item.children) {
                    const validChildren = item.children.filter((child) => isPermitted(child.perm));
                    if (validChildren.length > 0) {
                        return { ...item, children: validChildren };
                    }
                    return null;
                }
                return isPermitted(item.perm) ? item : null;
            })
            .filter(Boolean);
    };

    const navItems = filterNavItems(items);

    const content = (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    background: "#2563eb",
                    color: "#FFFFFF"
                }}
                className="admin-theme happy-admin-sidebar">
                {/* Header bar displaying logo centered from admin settings */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        px: 2,
                        py: 1.2,
                        minHeight: 56,
                        borderBottom: "1px solid rgba(255, 255, 255, 0.15)"
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <Logo style={{ maxHeight: "38px", margin: "0 auto" }} />
                    </Box>
                    {!lgUp && (
                        <IconButton
                            onClick={sendData}
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#ffffff",
                                p: 0.5,
                                borderRadius: "6px",
                                "&:hover": { background: "rgba(255, 255, 255, 0.2)" }
                            }}>
                            <CloseIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    )}
                </Box>
                <Box sx={{ flexGrow: 1, py: 0, px: 0, overflowY: "auto" }}>
                    <List component="nav" sx={{ pt: 0 }}>
                        {navItems.map((item) => (
                            <SidebarMenuItem key={item.title} item={item} level={0} onItemClick={handleNavItemClick} />
                        ))}
                    </List>
                </Box>
            </Box>
        </>
    );

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                open={open}
                PaperProps={{
                    sx: {
                        background: "#2563eb",
                        color: "#FFFFFF",
                        width: 240,
                        borderRight: "none",
                        boxShadow: "4px 0 16px rgba(0,0,0,0.08)"
                    }
                }}
                variant="persistent">
                {content}
            </Drawer>
        );
    }
    return (
        <Drawer
            anchor="left"
            onClose={sendData}
            open={open}
            PaperProps={{
                sx: {
                    background: "#2563eb",
                    color: "#FFFFFF",
                    width: 240,
                    borderRight: "none",
                    boxShadow: "4px 0 16px rgba(0,0,0,0.08)"
                }
            }}
            sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
            variant="temporary">
            {content}
        </Drawer>
    );
};
